import { X } from "lucide-react"
import {
  type ChangeEvent,
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

interface TagInputProps {
  /**
   * Field name — emitted on a single hidden `<input type="hidden" name="{name}">`
   * sibling carrying the **semicolon-joined** tag list (CTPO-locked CSV
   * format Q2 — backend stores tags as a single text column with `;`
   * delimiter, parses on read). Use `name="candidate[tags]"` from the
   * parent form so Rails reads `params[:candidate][:tags]` as a String.
   */
  name: string
  /** Initial tag list. Empty array on first paint. */
  defaultValue?: readonly string[]
  /** DOM id forwarded to the visible text input — consumers wire `<Label htmlFor>`. */
  id?: string
  /** `aria-describedby` chained to the visible input; same value also threaded to errors. */
  describedBy?: string
  /** Disable on offline / processing branches. */
  disabled?: boolean
  /** `aria-invalid` mirror — set when consumer surface holds an error. */
  invalid?: boolean
  /** Visible placeholder for the inline text input. */
  placeholder?: string
  /** Test id for the wrapper region (consumer drives the `data-testid` contract). */
  testId?: string
}

export interface TagInputHandle {
  /** Read the current list at submit time without re-syncing into a parent state. */
  getTags(): string[]
  /** Imperative reset hook for `onSuccess` flows that re-open the parent form. */
  reset(): void
}

/**
 * Tag chip-input — semicolon-tolerant multi-string entry used by the
 * single-add candidate dialog (`tags` field).
 *
 * Why a custom primitive (no shadcn `Combobox` / no library):
 *   1. **Spec defaults are simple.** Per UX Spec §Single-add and the
 *      CTPO-locked CSV format, tags are plain strings split on
 *      `,`, `;`, or `Enter`. There's no autocomplete source, no
 *      remote search — all the affordances `Combobox` provides cost
 *      complexity we don't pay back.
 *   2. **Accessibility owns the constraints.** Each chip carries a
 *      remove button with `aria-label` interpolating the tag value
 *      (`Remove tag {name}`); the visible input carries
 *      `aria-describedby` linking to the helper copy "Press Enter
 *      or comma to add". This matches the UX Spec §Accessibility
 *      contract verbatim.
 *   3. **Inertia wire shape.** We render a SINGLE hidden `<input>`
 *      carrying the **semicolon-joined** tag list (CTPO-locked CSV
 *      format Q2 + be-oks16 confirmation: backend stores tags as a
 *      single text column with `;` delimiter, parses on read).
 *      Backend reads `params[:candidate][:tags]` as a String and the
 *      model normalises further. The chip UI is purely a frontend
 *      concern — the wire stays simple.
 *
 * Tag normalisation on add:
 *   - trim whitespace
 *   - drop empties
 *   - dedupe (case-insensitive, last-write-wins on display)
 *   - split inputs containing `,` or `;` so paste of `beauty;skincare`
 *     becomes two chips
 *
 * Backspace on an empty input removes the last chip (standard chip-input
 * idiom; no destructive shortcut without context). The remove button is
 * always available as a non-keyboard fallback.
 *
 * Forwarded `ref` exposes `getTags()` + `reset()` so the caller can sync
 * the form on `onSuccess`. In the typical flow the single hidden input
 * already carries the data — `getTags` is a defence-in-depth read for
 * tests + a hook for "Add another" flows that re-open the parent form.
 */
export const TagInput = forwardRef<TagInputHandle, TagInputProps>(
  function TagInput(
    {
      name,
      defaultValue = [],
      id: providedId,
      describedBy,
      disabled,
      invalid,
      placeholder,
      testId,
    },
    ref,
  ) {
    const { t } = useTranslation()
    const generatedId = useId()
    const id = providedId ?? generatedId
    const [tags, setTags] = useState<string[]>(() =>
      normalise(Array.from(defaultValue)),
    )
    const [draft, setDraft] = useState("")
    const inputRef = useRef<HTMLInputElement | null>(null)

    useImperativeHandle(
      ref,
      () => ({
        getTags: () => tags,
        reset: () => {
          setTags([])
          setDraft("")
        },
      }),
      [tags],
    )

    const commitDraft = useCallback(
      (raw: string) => {
        const additions = splitOnDelimiters(raw)
        if (additions.length === 0) {
          setDraft("")
          return
        }
        setTags((current) => normalise([...current, ...additions]))
        setDraft("")
      },
      [setTags, setDraft],
    )

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
      const value = event.target.value
      // If the user typed/pasted a delimiter, commit immediately.
      if (/[,;]/.test(value)) {
        commitDraft(value)
        return
      }
      setDraft(value)
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
      if (event.key === "Enter") {
        // Prevent the Inertia <Form> from submitting on Enter while the
        // user is mid-tag — match the standard chip-input idiom.
        event.preventDefault()
        commitDraft(draft)
      } else if (event.key === "Backspace" && draft.length === 0) {
        // Backspace on empty input removes the last chip.
        if (tags.length === 0) return
        event.preventDefault()
        setTags((current) => current.slice(0, -1))
      }
    }

    function handleBlur() {
      if (draft.trim().length > 0) commitDraft(draft)
    }

    function removeTag(target: string) {
      setTags((current) =>
        current.filter((tag) => tag.toLowerCase() !== target.toLowerCase()),
      )
      // Restore focus to the input so keyboard users keep their place.
      inputRef.current?.focus()
    }

    return (
      <div
        className={cn(
          "focus-within:ring-ring/50 flex flex-wrap items-center gap-1.5 rounded-md border bg-transparent px-2 py-1.5 text-sm focus-within:ring-2",
          // OKS-1019 — the border colour is ONE exclusive branch. Invalid keeps
          // its red border while focused (a shared `focus-within:border-ring`
          // would out-rank the unvariant `border-destructive` and hide it); the
          // ring stays the ink token in both states (README rule 10).
          invalid
            ? "border-destructive"
            : "border-input focus-within:border-ring",
          disabled && "opacity-60",
        )}
        data-testid={testId}
        // Click anywhere in the chip area to focus the inline input.
        onClick={(event) => {
          if (event.target === event.currentTarget) inputRef.current?.focus()
        }}
      >
        {tags.map((tag) => (
          <Badge
            key={tag.toLowerCase()}
            variant="secondary"
            className="gap-1 pr-1"
            data-testid="candidate-input-tags-chip"
            data-tag={tag}
          >
            <span>{tag}</span>
            <button
              type="button"
              onClick={() => removeTag(tag)}
              disabled={disabled}
              className="hover:bg-background/40 -mr-0.5 inline-flex size-4 items-center justify-center rounded-sm focus-visible:outline focus-visible:outline-1 focus-visible:outline-current"
              aria-label={t("candidates.add.fields.tags.remove_aria", { tag })}
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </Badge>
        ))}
        <Input
          ref={inputRef}
          id={id}
          type="text"
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={tags.length === 0 ? placeholder : undefined}
          aria-describedby={describedBy}
          aria-invalid={invalid ? "true" : undefined}
          // Tame the shadcn Input default so it sits inline with the chips.
          // OKS-1028 — `max-md:min-h-0` is the ONE ratified opt-out from the
          // primitive's 44px mobile floor (BRIEF §4 D3). The chip WRAPPER is
          // the touch target (a click anywhere focuses this input) and already
          // clears 44px (`py-1.5` + this 36px row + border); flooring the inner
          // row too would make a ~58px pseudo-field. Any second opt-out needs
          // its own reason written into the BRIEF.
          className="border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 max-md:min-h-0"
          // No `name` here — submission goes via the single hidden input below.
        />
        {/*
         * Single hidden input carries the semicolon-joined value. Rails
         * reads `params[:candidate][:tags]` as a String and the model
         * stores it verbatim (text column with `;` delimiter — CTPO Q2).
         * Empty when no chips → Rails sees the empty string and skips
         * the assignment via the model's normalisation.
         */}
        <input type="hidden" name={name} value={tags.join(";")} />
      </div>
    )
  },
)

function splitOnDelimiters(raw: string): string[] {
  return raw
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
}

function normalise(input: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const tag of input) {
    const trimmed = tag.trim()
    if (trimmed.length === 0) continue
    const key = trimmed.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(trimmed)
  }
  return out
}
